/**
 * 作品操作按钮白名单配置
 * Module Action Whitelist
 *
 * 设计意图：
 * - 基于操作矩阵（specs/artwork-operation-strategy/spec.md）转换
 * - 白名单策略：仅明确列出的模块才显示对应按钮
 * - 新增模块默认不显示任何操作按钮，需显式授权
 * - TODO(v2.x): 服务端提供独立接口后，客户端白名单可简化或移除
 *
 * 与黑名单方案对比：
 * - 黑名单：新增模块默认显示，可能漏出未适配的操作按钮
 * - 白名单：新增模块默认隐藏，安全但需手动维护授权列表
 */

import { ModuleId, canvasModuleConfigurations } from "@xmiles/holopix-types";
import { SINGLE_IMAGE_MODULES, GROUP_IMAGE_MODULES, PSD_MODULES, COMFYUI_MODULES } from "./moduleListConstants";
import { MODULE_ID_CANVAS_DRAFT_A3, MODULE_ID_EXPLORE_A3 } from "@/constants/a3ModuleIds";

// ============================================================
// 基础模块分组（用于组合白名单）
// ============================================================

/**
 * 图片类基础模块
 * 图片 Tab 中所有作品共有的基础模块
 */
const IMAGE_BASE_MODULES = [
  ModuleId.Explore, // 1  一键成稿
  MODULE_ID_EXPLORE_A3, // 125 A3 一键成稿
  ModuleId.Colour, // 3  丰富细节
  ModuleId.Refine, // 5  局部细化
  ModuleId.Sketch, // 13 线稿提取
  ModuleId.Fission, // 14 相似图裂变
  ModuleId.Replace, // 18 涂抹替换
  ModuleId.Zoom, // 20 超清放大
  ModuleId.Expansion, // 22 智能拓图
  ModuleId.Pixelate, // 24 像素图转换
  ModuleId.Expression, // 28 表情编辑
  ModuleId.Render, // 29 万能渲染
  ModuleId.FreePerspective, // 33 自由视角
  ModuleId.Edit, // 智能编辑
  ModuleId.Matting, // 一键抠图
  ModuleId.ThreeTransform //3D模型生图
] as const;

/**
 * 3D 模块
 */
const THREE_D_MODULES = [
  ModuleId.GenerateThreeTransform // 11 图生3D
] as const;

/**
 * 悠船 Midjourney 模块（全集）
 * 旧：V7(66) / V8.1(67) / Niji7(68) / V8.2(104)
 * 新：阿里云 118–121
 */
const MJ_MODULES = [
  ModuleId.Midjourney, // 66
  ModuleId.MidjourneyV81, // 67
  ModuleId.MidjourneyNiji7, // 68
  ModuleId.MidjourneyV82, // 104
  ModuleId.AliyunYouchuan, // 118
  ModuleId.AliyunYouchuanV81, // 119
  ModuleId.AliyunYouchuanNiji7, // 120
  ModuleId.AliyunYouchuanV82Fastest, // 121
] as const;

/**
 * 可发布的视频模块（排除 Seedance Mini 79-82，Mini 作品暂不支持发布）
 * 含 MiniMax H3（83-86，Runninghub 自由画布视频）
 */
const PUBLISH_VIDEO_MODULES = [
  ModuleId.Aivideo, // 25 AI视频
  ModuleId.CanvasStartEndVideo, // 36 画布首尾帧视频
  ModuleId.CanvasImage2Video, // 43 画布图生视频
  ModuleId.CanvasReferenceVideo, // 37 画布参考图视频
  ModuleId.SeedanceStartEndVideo, // 55 Seedance 首尾帧
  ModuleId.SeedanceReferenceVideo, // 56 Seedance 全能参考
  ModuleId.SeedanceImage2Video, // 57 Seedance 图生视频
  ModuleId.SeedanceText2Video, // 64 Seedance 文生视频
  ModuleId.SeedanceMiniStartEndVideo, // 69 Seedance Mini 首尾帧
  ModuleId.SeedanceMiniReferenceVideo, // 70 Seedance Mini 参考
  ModuleId.SeedanceMiniImage2Video, // 71 Seedance Mini 图生视频
  ModuleId.SeedanceMiniText2Video, // 72 Seedance Mini 文生视频
  ModuleId.MinimaxH3StartEndVideo, // 83 自由画布-首尾帧视频MiniMax
  ModuleId.MinimaxH3ReferenceVideo, // 84 自由画布-参考生视频MiniMax
  ModuleId.MinimaxH3Image2Video, // 85 自由画布-图生视频MiniMax
  ModuleId.MinimaxH3Text2Video, // 86 自由画布-文生视频MiniMax
  ModuleId.MinimaxH3FastStartEndVideo, // 106 自由画布-首尾帧视频MiniMax Fast
  ModuleId.MinimaxH3FastReferenceVideo, // 107 自由画布-参考生视频MiniMax Fast
  ModuleId.MinimaxH3FastImage2Video, // 108 自由画布-图生视频MiniMax Fast
  ModuleId.MinimaxH3FastText2Video, // 109 自由画布-文生视频MiniMax Fast
  ModuleId.MinimaxH3MaxStartEndVideo, // 115 自由画布-首尾帧视频MiniMax 极速版
  ModuleId.MinimaxH3MaxImage2Video, // 116 自由画布-图生视频MiniMax 极速版
  ModuleId.MinimaxH3MaxText2Video, // 117 自由画布-文生视频MiniMax 极速版
] as const;

/**
 * 视频模块（全集，含 Seedance Mini 79-82）
 * 用于下载/上传等全量视频操作，但发布/画布编辑等特定操作需单独审慎
 */
const VIDEO_MODULES = [
  ModuleId.Aivideo, // 25 AI视频
  ModuleId.DownloadVideo, // 26 视频下载
  ModuleId.CanvasStartEndVideo, // 36 画布首尾帧视频
  ModuleId.CanvasImage2Video, // 43 画布图生视频
  ModuleId.CanvasReferenceVideo, // 37 画布参考图视频
  ModuleId.SeedanceStartEndVideo, // 55 Seedance 首尾帧
  ModuleId.SeedanceReferenceVideo, // 56 Seedance 全能参考
  ModuleId.SeedanceImage2Video, // 57 Seedance 图生视频
  ModuleId.SeedanceText2Video, // 64 Seedance 文生视频
  ModuleId.SeedanceMiniStartEndVideo, // 75 Seedance Mini 首尾帧
  ModuleId.SeedanceMiniReferenceVideo, // 76 Seedance Mini 参考
  ModuleId.SeedanceMiniImage2Video, // 77 Seedance Mini 图生视频
  ModuleId.SeedanceMiniText2Video, // 78 Seedance Mini 文生视频
  ModuleId.AiSeedanceMiniStartEndVideo, // 79 AI创作 Seedance Mini 首尾帧
  ModuleId.AiSeedanceMiniReferenceVideo, // 80 AI创作 Seedance Mini 参考
  ModuleId.AiSeedanceMiniImage2Video, // 81 AI创作 Seedance Mini 图生视频
  ModuleId.AiSeedanceMiniText2Video, // 82 AI创作 Seedance Mini 文生视频
  ModuleId.MinimaxH3StartEndVideo, // 83 自由画布-首尾帧视频MiniMax
  ModuleId.MinimaxH3ReferenceVideo, // 84 自由画布-参考生视频MiniMax
  ModuleId.MinimaxH3Image2Video, // 85 自由画布-图生视频MiniMax
  ModuleId.MinimaxH3Text2Video, // 86 自由画布-文生视频MiniMax
  ModuleId.MinimaxH3FastStartEndVideo, // 106 自由画布-首尾帧视频MiniMax Fast
  ModuleId.MinimaxH3FastReferenceVideo, // 107 自由画布-参考生视频MiniMax Fast
  ModuleId.MinimaxH3FastImage2Video, // 108 自由画布-图生视频MiniMax Fast
  ModuleId.MinimaxH3FastText2Video, // 109 自由画布-文生视频MiniMax Fast
  ModuleId.MinimaxH3MaxStartEndVideo, // 115 自由画布-首尾帧视频MiniMax 极速版
  ModuleId.MinimaxH3MaxImage2Video, // 116 自由画布-图生视频MiniMax 极速版
  ModuleId.MinimaxH3MaxText2Video, // 117 自由画布-文生视频MiniMax 极速版
] as const;

/**
 * 所有视频模块 ID（用于全局视频检测）
 */
export const ALL_VIDEO_MODULE_IDS: number[] = [...VIDEO_MODULES];

// ============================================================
// 操作按钮白名单
// ============================================================

/**
 * 【编辑复用】支持的模块
 * 条件：有生成参数 且 模块在白名单中
 */
export const REUSE_MODULE_WHITELIST: number[] = [...IMAGE_BASE_MODULES, ...THREE_D_MODULES];

/**
 * 【再迭代】支持的模块
 * 条件：模块在白名单中 且 有 modelList
 * 当前仅 Colour（图生图）支持再迭代
 */
export const ITERATE_MODULE_WHITELIST: number[] = [ModuleId.Colour];

/**
 * 【收藏】支持的模块
 * 图片 Tab 展示的所有模块（单图+组图+PSD）均自动可收藏，
 * 数据来源：moduleListConstants.ts 的子 Tab 模块名单（单一数据源）
 * 另外补充 AI 创作基础模块和 3D 模块（当前未在图片 Tab 展示但保留收藏能力）
 */
export const FAVORITE_MODULE_WHITELIST: number[] = [
  ...IMAGE_BASE_MODULES,
  ...THREE_D_MODULES,
  ...SINGLE_IMAGE_MODULES, // 含画布单图模块 + MJ 模块
  ...GROUP_IMAGE_MODULES,
  ...PSD_MODULES,
  ...COMFYUI_MODULES // 智能模型（module 35，帧图可收藏）
];

/**
 * 【复制】支持的模块
 * 排除：视频、3D、画布
 */
export const COPY_MODULE_WHITELIST: number[] = [...IMAGE_BASE_MODULES];

/**
 * 【发布】支持的模块（自由画布可发布作品）
 *
 * 设计依据：对齐旧版自由画布发布逻辑（src/constants 的 CANVAS_PUBLISH_MODULE_ID_WHITELIST）。
 * 我的资产图片 Tab 仅展示自由画布模块，故发布白名单只保留自由画布「生成类」模块；
 * 工具类（一键抠图/智能扩图/重绘放大/自由视角）、PSD、单图拆分暂不支持发布
 * （PRD：一键抠图等无重新编辑能力的作品不支持发布）。
 *
 * 视频作品发布：支持所有视频模块（AI视频 + 自由画布视频 + Seedance + Seedance Mini）
 * 发布时自动识别 mediaType=2，传递给发布接口。
 *
 * NOTE: 与 handleProjectPublish 联动——命中本白名单的模块发布时会传 fromFreeCanvas/moduleId，
 * 启用「隐藏参考图」选项与无提示词模块发布拦截（见 publishGuards.ts）。
 */
export const PUBLISH_MODULE_WHITELIST: number[] = [
  ModuleId.KleinFast, // 32 极速编辑Lite
  ModuleId.SeedDream, // 31 全能编辑V1
  ModuleId.Monkey, // 30 全能编辑V2
  ModuleId.MonkeyV21, // 131 全能编辑V2.1
  ModuleId.GptImage2, // 40 全能编辑V3
  ModuleId.GptImageV35, // 110 全能编辑V3.5 精绘
  ModuleId.GptImageV35Flare, // 122 全能编辑V3.5 极速
  ModuleId.GptImage2Enterprise, // 46 全能编辑V3企业版
  ModuleId.CanvasRender, // 34 自由画布-万能渲染
  ModuleId.CanvasDraft, // 47 自由画布-一键成稿
  MODULE_ID_CANVAS_DRAFT_A3, // 126 A3
  ModuleId.CanvasFission, // 45 自由画布-相似图裂变
  ModuleId.CanvasLocalRefine, // 63 自由画布-局部修改
  ModuleId.CanvasSeedreamV5Pro, // 74 自由画布-Seedream 5.0 Pro
  // 智能扩图（44）不在发布白名单：扩图结果不支持发布（产品口径 HP-9613，2026-09-21），
  // 与画布详情 ImageAction 的 PUBLISH_ARTWORK_MODULE_ID_WHITELIST（本就无 44）口径对齐
  ...MJ_MODULES, // 66/67/68 悠船 Midjourney
  ...PUBLISH_VIDEO_MODULES // 视频模块（不含 Seedance Mini 79-82）
];

/**
 * 【下载-倍数选择】关闭的模块
 * 我的资产当前仅展示画布模块，且画布模块结果图均为固定分辨率（相机参数/模板/size 驱动，不支持倍数放大），
 * 故全部画布模块直接下载原图，不弹倍数选择弹层。AI 创作模块暂不纳入（我的资产暂不展示）。
 */
export const CLOSE_MULTIPLES_DOWNLOAD_MODULES: number[] = canvasModuleConfigurations.map((m) => m.id);

/**
 * 【下载类型】映射
 * 根据模块判断下载类型
 */
export const DOWNLOAD_TYPE_MAP: Record<string, number[]> = {
  /** 3D 模型下载 */
  model_3d: THREE_D_MODULES as unknown as number[],
  /** 视频下载 */
  video: VIDEO_MODULES as unknown as number[],
  /** 图片下载（默认） */
  image: IMAGE_BASE_MODULES as unknown as number[]
};

// ============================================================
// "用作参考图"操作区白名单（底部 ReferenceActionBar）
// ============================================================

/** 画布视频模块（Vidu + Seedance + Seedance Mini + MiniMax H3） */
export const CANVAS_VIDEO_MODULES = [
  ModuleId.CanvasStartEndVideo, // 36
  ModuleId.CanvasImage2Video, // 43
  ModuleId.CanvasReferenceVideo, // 37
  ModuleId.SeedanceStartEndVideo, // 55
  ModuleId.SeedanceReferenceVideo, // 56
  ModuleId.SeedanceImage2Video, // 57
  ModuleId.SeedanceText2Video, // 64
  ModuleId.SeedanceMiniStartEndVideo, // 69
  ModuleId.SeedanceMiniReferenceVideo, // 70
  ModuleId.SeedanceMiniImage2Video, // 71
  ModuleId.SeedanceMiniText2Video, // 72
  ModuleId.MinimaxH3StartEndVideo, // 83
  ModuleId.MinimaxH3ReferenceVideo, // 84
  ModuleId.MinimaxH3Image2Video, // 85
  ModuleId.MinimaxH3Text2Video, // 86
  ModuleId.MinimaxH3FastStartEndVideo, // 106
  ModuleId.MinimaxH3FastReferenceVideo, // 107
  ModuleId.MinimaxH3FastImage2Video, // 108
  ModuleId.MinimaxH3FastText2Video, // 109
  ModuleId.MinimaxH3MaxStartEndVideo, // 115
  ModuleId.MinimaxH3MaxImage2Video, // 116
  ModuleId.MinimaxH3MaxText2Video, // 117
] as const;

/**
 * 【去画布编辑】支持的模块
 * Canvas 模块可将结果图导入画布继续编辑
 * 基于 CANVAS_MODULE_WHITELIST + 手动维护的缺失模块
 */
export const GO_CANVAS_EDIT_MODULE_WHITELIST: number[] = [
  // V3-企业专线
  ModuleId.GptImage2Enterprise,
  // V3.5 精绘
  ModuleId.GptImageV35,
  // V3.5 极速
  ModuleId.GptImageV35Flare,
  // V3-普通版
  ModuleId.GptImage2,
  // Monkey
  ModuleId.Monkey,
  // 全能编辑 V2.1
  ModuleId.MonkeyV21,
  // 豆包
  ModuleId.SeedDream,
  // 极速编辑
  ModuleId.KleinFast,
  // 自由画布-万能渲染
  ModuleId.CanvasRender,
  // 自由画布视频生成
  ...CANVAS_VIDEO_MODULES,
  // 自由画布-相似图裂变
  ModuleId.CanvasFission,
  // 自由画布-一键抠图
  ModuleId.CanvasMatting,
  // 自由画布-智能扩图
  ModuleId.VisualExpansionV2,
  // 自由画布局部修改
  ModuleId.CanvasLocalRefine,
  // 自由画布-一键成稿（含 A3）
  ModuleId.CanvasDraft,
  MODULE_ID_CANVAS_DRAFT_A3,
  // 自由画布-自由视角
  ModuleId.CanvasQwenFreePerspective,
  // 自由画布-重绘放大
  ModuleId.CanvasRepaintUpscale,
  // 自由画布-Seedream 5.0 Pro
  ModuleId.CanvasSeedreamV5Pro,
  // 自由画布-组图（雪碧图拆分）
  ModuleId.CanvasComfyUIMulti,
  // 自由画布-智能模型（module 35，ComfyUI 模板）不提供去画布编辑：模板记录无参考图编辑语义（用户拍板）
  // 悠船 Midjourney
  ...MJ_MODULES
];

/**
 * 【重新编辑】排除的模块
 * 这些模块需特定画布面板操作，无法通过底部输入栏回填参数
 * 排除后，其余模块均可"重新编辑"
 */
export const REEDIT_EXCLUDED_MODULES: number[] = [
  ModuleId.CanvasComfyUIMulti, // 画布组图
  ModuleId.CanvasComfyUIPSD, // 画布PSD（需特定面板，不支持底部输入栏回填）
  ModuleId.CanvasComfyUISingle, // 画布智能模型（module 35，ComfyUI 模板）：与聊天详情二级操作区整块隐藏口径一致
  ModuleId.CanvasMatting, // 一键抠图（画布版）
  ModuleId.CanvasRepaintUpscale, // 画布重绘放大
  ModuleId.GameUiSplit, // 全能拆分（87）
  ModuleId.KleinConsistencyEdit, // 图层修补（88）
  ModuleId.KleinConsistencyImageRepair, // Klein 图像修补（89）
  ModuleId.VisualExpansionV2 // 智能扩图：无输入栏回填语义，与画布详情隐藏「重新编辑」同口径（扩图参数回填走「去画布编辑」）
];

/**
 * 【再次生成】支持的模块
 * 标准化生成模块，点击后在画布中自动触发生成
 */
export const REGENERATE_MODULE_WHITELIST: number[] = [
  // SUPPORTED_CHAT_MODULE_IDS 等价
  ModuleId.GptImage2,
  ModuleId.GptImageV35,
  ModuleId.GptImageV35Flare,
  ModuleId.GptImage2Enterprise,
  ModuleId.Monkey,
  ModuleId.MonkeyV21,
  ModuleId.SeedDream,
  ModuleId.KleinFast,
  // 画布渲染/视频/成稿/局部修改/自由视角
  ModuleId.CanvasRender,
  ...CANVAS_VIDEO_MODULES,
  ModuleId.CanvasDraft,
  MODULE_ID_CANVAS_DRAFT_A3,
  ModuleId.CanvasLocalRefine,
  ModuleId.CanvasQwenFreePerspective,
  ModuleId.CanvasSeedreamV5Pro,
  ModuleId.CanvasComfyUIPSD, // 画布PSD（走标准路径拿参数重新生成）
  // 阿里云悠船（118–121）。旧通道 66/67/68/104 历史记录不提供再次生成
  ModuleId.AliyunYouchuan,
  ModuleId.AliyunYouchuanV81,
  ModuleId.AliyunYouchuanNiji7,
  ModuleId.AliyunYouchuanV82Fastest,
];

// ============================================================
// 辅助函数
// ============================================================

/**
 * 检查模块是否在白名单中
 * @param moduleId - 模块ID
 * @param whitelist - 白名单数组
 */
export function isModuleInWhitelist(moduleId: number, whitelist: number[]): boolean {
  return whitelist.includes(moduleId);
}

/**
 * 获取模块的下载类型
 * @param moduleId - 模块ID
 * @returns 下载类型: 'image' | 'model_3d' | 'video'
 */
export function getDownloadTypeByModule(moduleId: number): "image" | "model_3d" | "video" {
  if ((DOWNLOAD_TYPE_MAP.model_3d as number[]).includes(moduleId)) return "model_3d";
  if ((DOWNLOAD_TYPE_MAP.video as number[]).includes(moduleId)) return "video";
  return "image";
}

/**
 * 是否关闭倍数下载
 * @param moduleId - 模块ID
 */
export function isCloseMultiplesDownload(moduleId: number): boolean {
  return CLOSE_MULTIPLES_DOWNLOAD_MODULES.includes(moduleId);
}
